<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import type { IResetAptitudeGroup } from '@manage/shared-types';
import { useAssessmentsStore } from '@/stores/assessments';
import { formatDate } from '@/utils/formatters';

interface Props {
  visible: boolean;
  parentAssessmentId: number | null;
  userId: number | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void;
  (e: 'reset-complete'): void;
}>();

const toast = useToast();
const assessmentsStore = useAssessmentsStore();

const selectedIds = ref<number[]>([]);
const showConfirmation = ref(false);

const isVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
});

const modalData = computed(() => assessmentsStore.resetModalData);
const isLoading = computed(() => assessmentsStore.resetModalLoading);
const error = computed(() => assessmentsStore.resetModalError);
const isResetting = computed(() => assessmentsStore.resetInProgress);

const aptitudes = computed(() => modalData.value?.aptitudes ?? []);

const allChildren = computed(() => aptitudes.value.flatMap((apt) => apt.children));

const allSelected = computed(() => {
  return (
    allChildren.value.length > 0 &&
    allChildren.value.every((child) => selectedIds.value.includes(child.id))
  );
});

const someSelected = computed(() => {
  return selectedIds.value.length > 0 && selectedIds.value.length < allChildren.value.length;
});

const toggleSelectAll = () => {
  if (allSelected.value) {
    selectedIds.value = [];
  } else {
    selectedIds.value = allChildren.value.map((child) => child.id);
  }
};

const isGroupAllSelected = (group: IResetAptitudeGroup): boolean => {
  return group.children.every((child) => selectedIds.value.includes(child.id));
};

const isGroupSomeSelected = (group: IResetAptitudeGroup): boolean => {
  const count = group.children.filter((child) => selectedIds.value.includes(child.id)).length;

  return count > 0 && count < group.children.length;
};

const toggleGroup = (group: IResetAptitudeGroup) => {
  const groupIds = group.children.map((child) => child.id);

  if (isGroupAllSelected(group)) {
    selectedIds.value = selectedIds.value.filter((id) => !groupIds.includes(id));
  } else {
    const newIds = groupIds.filter((id) => !selectedIds.value.includes(id));

    selectedIds.value = [...selectedIds.value, ...newIds];
  }
};

const formatTime = (seconds: number | null): string => {
  if (seconds === null) {
    return 'N/A';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getScoreTagSeverity = (score: number | null): string => {
  if (score === null) {
    return 'contrast';
  }

  if (score >= 80) {
    return 'success';
  }

  if (score >= 60) {
    return 'info';
  }

  return 'warn';
};

const showConfirmationStep = () => {
  showConfirmation.value = true;
};

const cancelConfirmation = () => {
  showConfirmation.value = false;
};

const handleReset = async () => {
  if (!props.parentAssessmentId || selectedIds.value.length === 0) {
    return;
  }

  try {
    const result = await assessmentsStore.resetChildAssessments(
      props.parentAssessmentId!,
      selectedIds.value
    );

    toast.add({
      severity: 'success',
      summary: 'Assessments Reset',
      detail: result.message || `Successfully reset ${result.resetCount} assessment(s)`,
      life: 5000,
    });

    emit('reset-complete');
    handleClose();
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Reset Failed',
      detail: assessmentsStore.resetModalError || 'Failed to reset assessments',
      life: 5000,
    });
  }
};

const handleClose = () => {
  selectedIds.value = [];
  showConfirmation.value = false;
  assessmentsStore.clearResetModalState();
  isVisible.value = false;
};

watch(
  () => props.visible,
  async (newVisible) => {
    if (newVisible && props.parentAssessmentId) {
      selectedIds.value = [];
      showConfirmation.value = false;
      try {
        await assessmentsStore.fetchChildAssessmentsForReset(props.parentAssessmentId);
      } catch {
        // Error is handled by store
      }
    }
  }
);
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :closable="!isResetting"
    :dismissableMask="!isResetting"
    :closeOnEscape="!isResetting"
    :style="{ width: '700px', maxWidth: '95vw' }"
    data-cy="reset-assessments-modal"
    @hide="handleClose"
  >
    <template #header>
      <div class="modal-header">
        <h2>Reset {{ modalData?.parentAssessment?.displayName || 'Assessment' }}</h2>
        <div v-if="modalData?.parentAssessment" class="user-name">
          <strong>User:</strong>
          {{ modalData.parentAssessment.userName }}
        </div>
      </div>
    </template>

    <div v-if="isLoading" class="loading-container" data-cy="reset-modal-loading">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <span>Loading assessments...</span>
    </div>

    <Message
      v-else-if="error"
      severity="error"
      icon="pi pi-exclamation-triangle"
      data-cy="reset-modal-error"
    >
      {{ error }}
    </Message>

    <div v-else-if="modalData" class="modal-content" data-cy="reset-modal-content">
      <!-- Confirmation view -->
      <div v-if="showConfirmation" class="confirmation-view">
        <p role="alert" data-cy="reset-confirmation-message">
          Are you sure you want to reset <strong>{{ selectedIds.length }}</strong>
          assessment(s)? This cannot be undone, and the user will have to take the assessment again.
        </p>
      </div>

      <!-- Selection view -->
      <template v-else>
        <Message
          severity="warn"
          icon="pi pi-exclamation-triangle"
          :closable="false"
          class="warning-banner"
          data-cy="reset-warning"
        >
          <strong>Warning:</strong> This action should only be done if significant time has passed
          since the original assessment. Resetting too soon may result in invalid results.
        </Message>

        <div v-if="aptitudes.length === 0" class="no-assessments" data-cy="no-assessments-message">
          <Message severity="info"> No completed assessments available to reset. </Message>
        </div>

        <div v-else class="assessments-list">
          <div class="select-all-row" data-cy="select-all-row">
            <Checkbox
              :modelValue="allSelected"
              :binary="true"
              :indeterminate="someSelected"
              inputId="select-all"
              data-cy="select-all-checkbox"
              @update:model-value="toggleSelectAll"
            />
            <label for="select-all" class="select-all-label">Select All</label>
          </div>

          <div
            v-for="(aptitude, aptIndex) in aptitudes"
            :key="aptitude.macroQuestionTypeId"
            class="assessment-group"
            :data-cy="`aptitude-group-${aptitude.macroQuestionTypeId}`"
          >
            <div class="group-header" :data-cy="`aptitude-header-${aptitude.macroQuestionTypeId}`">
              <Checkbox
                :modelValue="isGroupAllSelected(aptitude)"
                :binary="true"
                :indeterminate="isGroupSomeSelected(aptitude)"
                :inputId="`aptitude-${aptIndex}`"
                :data-cy="`aptitude-checkbox-${aptitude.macroQuestionTypeId}`"
                @update:model-value="toggleGroup(aptitude)"
              />
              <label :for="`aptitude-${aptIndex}`" class="group-name">
                {{ aptitude.name }}
              </label>
              <Tag
                v-if="aptitude.macroScorePercentage !== null"
                class="score-badge"
                :value="`${aptitude.macroScorePercentage}%`"
                :severity="getScoreTagSeverity(aptitude.macroScorePercentage)"
              />
            </div>
            <div
              v-for="child in aptitude.children"
              :key="child.id"
              class="assessment-row assessment-row--indented"
              :data-cy="`assessment-row-${child.id}`"
            >
              <div class="assessment-checkbox">
                <Checkbox
                  v-model="selectedIds"
                  :value="child.id"
                  :inputId="`child-${child.id}`"
                  :data-cy="`checkbox-${child.id}`"
                />
              </div>
              <label :for="`child-${child.id}`" class="assessment-info">
                <span class="assessment-name">{{ child.displayName }}</span>
                <div class="assessment-details">
                  <div class="detail-item">
                    <span class="detail-label">Score:</span>
                    <span class="detail-value">
                      <Tag
                        v-if="child.scorePercentage !== null"
                        class="score-badge"
                        :value="`${child.scorePercentage}%`"
                        :severity="getScoreTagSeverity(child.scorePercentage)"
                      />
                      <span v-else class="no-score">N/A</span>
                    </span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Completed:</span>
                    <span class="detail-value">{{ formatDate(child.completedAt) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Time:</span>
                    <span class="detail-value">{{ formatTime(child.timeUsedSeconds) }}</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <div class="modal-footer">
        <template v-if="showConfirmation">
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            :disabled="isResetting"
            data-cy="reset-confirm-cancel-btn"
            @click="cancelConfirmation"
          />
          <Button
            label="Reset"
            severity="danger"
            :loading="isResetting"
            :disabled="isResetting"
            data-cy="reset-confirm-reset-btn"
            @click="handleReset"
          />
        </template>
        <template v-else>
          <Button
            label="Cancel"
            severity="secondary"
            outlined
            :disabled="isResetting"
            data-cy="cancel-reset-btn"
            @click="handleClose"
          />
          <Button
            label="Reset Selected"
            severity="danger"
            :loading="isResetting"
            :disabled="selectedIds.length === 0 || isResetting"
            data-cy="confirm-reset-btn"
            @click="showConfirmationStep"
          />
        </template>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.modal-header {
  display: flex;
  flex-direction: column;
  gap: 4px;

  h2 {
    margin: 0;
    padding: 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  .user-name {
    color: var(--p-text-muted-color);
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px;
  color: var(--p-text-muted-color);
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.warning-banner {
  margin: 0;
}

.no-assessments {
  padding: 20px 0;
}

.assessments-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.select-all-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: var(--p-surface-100);
  @include rounded-corners($md-radius);
  flex-shrink: 0;

  .select-all-label {
    font-weight: 600;
    cursor: pointer;
  }
}

.assessment-group {
  border: 1px solid var(--p-surface-200);
  @include rounded-corners($md-radius);
  overflow: hidden;
  flex-shrink: 0;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: var(--p-surface-50);
  border-bottom: 1px solid var(--p-surface-200);

  .group-name {
    font-weight: 600;
    cursor: pointer;
  }
}

.assessment-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--p-surface-200);
  @include rounded-corners($md-radius);
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--p-surface-50);
  }

  &--indented {
    border: none;
    @include rounded-corners(0);
    padding-left: 40px;
    border-bottom: 1px solid var(--p-surface-100);

    &:last-child {
      border-bottom: none;
    }
  }
}

.assessment-checkbox {
  padding-top: 2px;
}

.assessment-info {
  flex: 1;
  cursor: pointer;
}

.assessment-name {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
}

.assessment-details {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
}

.detail-label {
  color: var(--p-text-muted-color);
}

.detail-value {
  color: var(--p-text-color);
}

.score-badge {
  font-weight: 600;
  font-size: 0.75rem;
}

.no-score {
  color: var(--p-text-muted-color);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
